import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  SessionQualityDetectionsFieldsFragment, SessionSummaryFieldsFragment, useGetProjectComplianceQuery, useGetSessionQualityDetectionsQuery,
} from '../../../../../../../../../../generated/core-api/graphql-react-query';
import TopicTag from '../../../../../CommandPage/Components/CommandItem/Components/TopicTag/TopicTag';
import withSessionDetailsSectionErrorBoundary from '../SessionDetailsSectionErrorBoundary';
import DetectionQualityItem from './DetectionQualityItem';
import StyledBox from './styles';

export interface TopicWithDetections {
  topic: { text: string, color: string, textLower: string };
  detections: SessionQualityDetectionsFieldsFragment[];
}

interface SessionQualityProps {
  session: SessionSummaryFieldsFragment;
}

function SessionQuality(props: SessionQualityProps) {
  const { session } = props;
  const sessionQualityData = useGetSessionQualityDetectionsQuery({ input: { id: session.id } });
  const sessionQuality = sessionQualityData.data?.getSession;
  const { t } = useTranslation('session');
  const project = useGetProjectComplianceQuery({ input: { id: session.projectId } });
  const qualityTopics = project.data?.getProject.formulationTopics || [];
  const agentDetections = sessionQuality?.detections.filter((d) => !d.isFromClient) || [];
  const results: TopicWithDetections[] = qualityTopics.map((qt) => {
    const triggers = qt.topic.projectVocalTriggers?.items ?? [];
    if (!sessionQuality) return null;
    const detections = agentDetections.filter((d) => triggers.some((pt) => pt.triggerId === d.commandId));
    return {
      topic: {
        text: qt.topic.text,
        color: qt.topic.color,
        textLower: qt.topic.textLower,
      },
      detections,
    };
  }).filter((r): r is TopicWithDetections => !!r && r.detections.length > 0);

  if (!results.length) return null;
  return (
    <StyledBox>
      <Typography variant="subtitle1">
        {t('details.quality.title')}
      </Typography>
      <Box className="content">
        {results.map((result) => (
          <Box key={result.topic.textLower}>
            <TopicTag topic={result.topic.text} color={result.topic.color} />
            <Box>
              {result.detections.map((detection) => (
                <DetectionQualityItem
                  key={detection.id}
                  detection={detection}
                  sessionCreatedAt={session.date}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </StyledBox>
  );
}

export default withSessionDetailsSectionErrorBoundary(SessionQuality, 'session quality');
